import { useEffect, useState, Suspense, lazy } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import PreLoader from "../../components/preloader/PreLoader";

const VisaServices = lazy(() => import("../../pages/typesofvisa/typesOfVisa/typesOfVisa"));
const ServiceDetailPage = lazy(() => import("../serviceDetails/ServiceDetailPage"));
const DepartmentPages = lazy(() => import("../Department/DepartmentPages"));
const LegalTranslationDetail = lazy(() => import("../../pages/LegalTranslation/LegalTranslationDetail/LegalTranslationDetail"));

import { getFooterPageTypeBySlug } from "../../lib/api";
import { useCart } from "../../hooks/useCart";

const apiUrl = import.meta.env.VITE_API_URL;

const ConditionalComponent = () => {
  const { getData } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  const [pageType, setPageType] = useState<
    "visa" | "department" | "new-visa" | "service" | "legal" | null
  >(null);

  const [componentName, setComponentName] = useState<
    "VisaServices" | "ServiceDetailPage" | "DepartmentPages" | "LegalTranslationDetail" | null
  >(null);

  const [loading, setLoading] = useState(true);

  const fullPath = location.pathname;

  // Map component names to actual React components
  const componentsMap: Record<string, React.ElementType> = {
    VisaServices,
    ServiceDetailPage,
    DepartmentPages,
    LegalTranslationDetail,
  };

  useEffect(() => {
    if (!fullPath) return;

    getData();

    const fetchPageType = async () => {
      setLoading(true);
      try {
        const dataUrl = `${apiUrl}/api/footer-pages/check-page-type?path=${encodeURIComponent(fullPath)}`;
        const data = await getFooterPageTypeBySlug(dataUrl);

        let detectedType = data?.type || "";
        let compName = data?.componentName || null;

        // Auto-detect legal pages by path
        const pathLower = fullPath.toLowerCase();
        if (
          pathLower.includes("legal") ||
          pathLower.includes("translation") ||
          pathLower.includes("legal-translation")
        ) {
          detectedType = "legal";
          compName = "LegalTranslationDetail";
        }

        if (["new-visa", "visa", "department", "service", "legal"].includes(detectedType)) {
          setPageType(detectedType as typeof pageType);
          setComponentName(compName as typeof componentName);
          window.sessionStorage.setItem("isLoad", "0");
        } else {
          navigate("/notFound");
        }
      } catch (error) {
        console.error("Error fetching page type:", error);
        navigate("/notFound");
      } finally {
        setLoading(false);
      }
    };

    fetchPageType();
  }, [fullPath, navigate]);

  if (loading || pageType === null || componentName === null) return <PreLoader />;

  const DynamicComponent = componentsMap[componentName];

  return (
    <Suspense fallback={<PreLoader />}>
      {DynamicComponent ? <DynamicComponent key={componentName} /> : <PreLoader />}
    </Suspense>
  );
};

export default ConditionalComponent;